import { NORDIC_REVIEW_REPO, NORDIC_REVIEW_TARGET_HOURS } from '@skystone/shared';

/**
 * How the two review-flow metrics are calculated (PRODOPSAI-679).
 *
 * Both clock explanations are shown regardless of which is selected, so the
 * reader can see what the other one would mean without toggling. The worked
 * Friday→Monday example is real: 2 office hours against 65 calendar hours.
 */
export function NordicReviewMethodology() {
  return (
    <details
      className="rounded-lg border border-border bg-muted/30 p-3 text-[11px] text-muted-foreground"
      data-testid="nordic-review-methodology"
      open
    >
      <summary className="cursor-pointer text-xs font-semibold text-foreground">
        How this is calculated
      </summary>

      <div className="mt-2 space-y-2">
        <ul className="list-inside list-disc space-y-1">
          <li>
            Source: GitHub API, <code>{NORDIC_REVIEW_REPO}</code>
          </li>
          <li>
            <span className="font-medium text-foreground">Both clocks start when the PR
            becomes reviewable.</span>{' '}
            For a PR opened as a draft, that is the moment it is marked{' '}
            <em>Ready for review</em>. For a PR opened normally, there is no draft stage, so the
            clock starts the moment it is opened. Time spent as a draft is never counted — that
            is the author still writing the code, not anyone waiting.
          </li>
          <li>
            <span className="font-medium text-foreground">Time to First Review</span> — from that
            point until the first review by another person (Approve, Request changes, or
            Comment).
          </li>
          <li>
            <span className="font-medium text-foreground">Time to Merge</span> — from that point
            until the PR is merged.
          </li>
          <li>
            <span className="font-medium text-foreground">We do not count reviews by bots</span>,
            or by the PR&apos;s own author.
          </li>
          <li>We attribute each PR to the GitHub team of the person who opened it.</li>
          <li>
            <span className="font-medium text-foreground">
              We show the median and the 75th percentile.
            </span>{' '}
            The median is the typical PR; p75 shows the slow quarter. A median alone hides the
            tail — in one recent month a team had a median of 2.8h against a 90th percentile of
            99h.
          </li>
        </ul>

        <div className="space-y-1 border-l-2 border-border pl-3">
          <p>
            <span className="font-medium text-foreground">Office hours</span> — the clock runs
            08:00–17:00, Monday to Friday. Nights and weekends don&apos;t count. A PR marked ready
            at 16:00 on Friday and reviewed at 09:00 on Monday waited{' '}
            <span className="font-medium text-foreground">2 hours</span>.
          </p>
          <p>
            <span className="font-medium text-foreground">Calendar hours</span> — wall-clock time,
            everything counts. The same PR waited{' '}
            <span className="font-medium text-foreground">65 hours</span>.
          </p>
          <p>
            The Nordic teams&apos; goal tracker uses office hours, and its targets — under{' '}
            {NORDIC_REVIEW_TARGET_HOURS.firstReview}h to first review, under{' '}
            {NORDIC_REVIEW_TARGET_HOURS.merge}h to merge — are set against that clock.
            Skystone&apos;s other engineering metrics, including PR Lead Time in the DORA cards
            above, use calendar hours, which is what DORA benchmarks assume. Both are available
            here so the figures can be read against either.
          </p>
          <p className="italic">
            Public holidays are counted as working days, so office-hours figures run slightly high
            around Swedish holidays.
          </p>
        </div>
      </div>
    </details>
  );
}
